import React, { useEffect, useState, useMemo } from "react";
import Wrapper from "../../../components/Wrapper";
import { hardwareApi } from "../../../services/labApi";

export default function HardwareCapacityPage() {
  const [capacityData, setCapacityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const data = await hardwareApi.getHardwareCapacity();
      setCapacityData(data);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load capacity data. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fullSet = useMemo(() => {
    return capacityData?.full_set_capacity || {
        max_capacity: 0,
        limiting_component: "Unknown",
    };
    }, [capacityData]);

  const individuals = useMemo(() => {
    return capacityData?.individual_capacities || [];
    }, [capacityData]);

  const deviceCap = individuals.find(h => String(h.hardware_id) === 'HW0001001' || h.name?.toLowerCase().includes('device')) || { max_capacity: 0 };
  const shakerCap = individuals.find(h => String(h.hardware_id) === 'HW0001002' || h.name?.toLowerCase().includes('shaker')) || { max_capacity: 0 };
  const pumpCap   = individuals.find(h => String(h.hardware_id) === 'HW0001003' || h.name?.toLowerCase().includes('pump')) || { max_capacity: 0 };

  const limitingComponents = useMemo(() => {
    const limiters = new Set();
    
    if (fullSet.limiting_component && !fullSet.limiting_component.includes('No components')) {
      limiters.add(fullSet.limiting_component);
    }
    
    individuals.forEach((ind) => {
      if (ind.limiting_component && !ind.limiting_component.includes('No components') && ind.max_capacity === 0) {
        limiters.add(ind.limiting_component);
      }
    });
    
    return Array.from(limiters);
  }, [fullSet, individuals]);

  if (loading) {
    return (
      <Wrapper>
        <div className="flex items-center justify-center h-64 text-slate-500">Loading capacity data...</div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="w-full text-center sm:text-left">
          <h2 className="m-0 text-2xl font-bold text-slate-800">Hardware Capacity</h2>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100">
            {error}
          </div>
        )}

        {/* --- TOP SUMMARY CARDS --- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-sm text-slate-500 font-medium mb-1">Full Set Capacity</p>
            <h3 className="text-4xl font-bold text-slate-800">{fullSet.max_capacity}</h3>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-sm text-slate-500 font-medium mb-1">Device Capacity</p>
            <h3 className="text-4xl font-bold text-slate-800">{deviceCap.max_capacity}</h3>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-sm text-slate-500 font-medium mb-1">Shaker Capacity</p>
            <h3 className="text-4xl font-bold text-slate-800">{shakerCap.max_capacity}</h3>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-sm text-slate-500 font-medium mb-1">Pump Capacity</p>
            <h3 className="text-4xl font-bold text-slate-800">{pumpCap.max_capacity}</h3>
          </div>
        </div>

        {/* --- LIMITING COMPONENTS SECTION --- */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h4 className="text-lg font-semibold text-slate-800 mb-4">Limiting Components</h4>
          {limitingComponents.length === 0 ? (
            <p className="text-sm text-slate-500">No limiting components detected (BOMs might be empty or stock is healthy).</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {limitingComponents.map((componentName, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-sm font-medium"
                >
                  {componentName}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* --- DETAILED BREAKDOWN SECTION --- */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h4 className="text-lg font-semibold text-slate-800 mb-4">Detailed Breakdown</h4>
          
          <div className="space-y-3">
            {individuals.map((hw) => (
              <div key={hw.hardware_id} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-baseline gap-2">
                  <p className="font-semibold text-slate-800 text-base">{hw.name}</p>
                  {hw.version_tag && (
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-semibold rounded border border-blue-100">
                      {hw.version_tag}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="text-slate-600">
                    Sets supported: <span className="font-semibold text-slate-800">{hw.max_capacity}</span>
                  </span>
                  
                  {hw.limiting_component && !hw.limiting_component.includes('No components') && (
                    <span className="text-slate-500">
                      Limiting Component: <span className="text-amber-600 font-medium">{hw.limiting_component}</span>
                    </span>
                  )}
                  
                  {hw.limiting_component?.includes('No components') && (
                    <span className="text-slate-400 italic">
                      No components defined in BOM
                    </span>
                  )}
                </div>
              </div>
            ))}

            {individuals.length === 0 && (
              <div className="text-slate-500 text-sm py-4">No hardware data found.</div>
            )}
          </div>
        </div>

      </div>
    </Wrapper>
  );
}